import {
  Circle,
  Document,
  Line,
  Page,
  Path,
  Rect,
  Svg,
  Text,
  View,
} from "@react-pdf/renderer";
import { MarkdownPdf } from "components/Markdown";
import { parseDate, parseTime } from "helpers/date";
import { centsToDollars, parsePrice } from "helpers/price";
import { IOrder } from "types/Order/Order";
import { IOrderHistory } from "types/OrderHistory";
import {
  ITermsAcceptance,
  TermsAcceptanceContext,
} from "types/TermsAcceptance/TermsAcceptance";

import { Footer } from "../ClearOrderDocument/partials/Footer";
import { Header } from "../ClearOrderDocument/partials/Header";
import { styles } from "../ClearOrderDocument/styles";
import {
  hasCommunicationHistory,
  OrderHistorySection,
} from "./OrderHistorySection";
import { PaymentSummarySection } from "./PaymentSummarySection";

const ACCEPTANCE_CONTEXT_LABELS: Record<TermsAcceptanceContext, string> = {
  [TermsAcceptanceContext.FullPayment]: "Full Payment",
  [TermsAcceptanceContext.DownPayment]: "Down Payment",
  [TermsAcceptanceContext.PayRemaining]: "Pay Remaining Amount",
};

interface ITermsAcceptancePdfProps {
  termsAcceptance: ITermsAcceptance;
  order: IOrder;
  orderHistories: IOrderHistory[];
  patientFirstName: string;
  patientLastName: string;
  patientDateOfBirth: string;
  facilityLogo?: string;
  fullAddress?: string;
  phoneNumber?: string;
  facilityName?: string;
  cardInfo?: { brand: string; last4: string };
}
function InfoIcon() {
  return (
    <Svg width={12} height={12} viewBox="0 0 24 24">
      <Circle
        cx={12}
        cy={12}
        r={11}
        stroke="#706f7b"
        strokeWidth={1.5}
        fill="none"
      />
      <Circle cx={12} cy={7.5} r={1.2} fill="#706f7b" />
      <Line
        x1={12}
        y1={11}
        x2={12}
        y2={17}
        stroke="#706f7b"
        strokeWidth={1.8}
        strokeLineCap="round"
      />
    </Svg>
  );
}

function LockIcon() {
  return (
    <Svg width={10} height={10} viewBox="0 0 24 24">
      <Path
        d="M7 11 V8 a5 5 0 0 1 10 0 V11"
        stroke="#858490"
        strokeWidth={2}
        fill="none"
        strokeLineCap="round"
      />
      <Rect x={5} y={11} width={14} height={10} rx={2} ry={2} fill="#858490" />
    </Svg>
  );
}

function CheckedBox() {
  return (
    <Svg width={12} height={12} viewBox="0 0 24 24">
      <Rect
        x={1}
        y={1}
        width={22}
        height={22}
        rx={4}
        ry={4}
        fill="#5b4dfd"
        stroke="#5b4dfd"
      />
      <Path
        d="M6 12 L10.5 16.5 L18 8"
        stroke="#ffffff"
        strokeWidth={2.5}
        fill="none"
        strokeLineCap="round"
        // strokeLineJoin="round"
      />
    </Svg>
  );
}

function InfoRow({
  label,
  value,
  width,
}: {
  label: string;
  value: string;
  width?: number;
}) {
  return (
    <View
      style={{
        display: "flex",
        flexDirection: "row",
        alignItems: "flex-start",
        marginBottom: 3,
      }}
    >
      <Text style={{ ...styles.listTitle, width: width || 100 }}>{label}</Text>
      <Text style={{ ...styles.smallText, flex: 1 }}>{value}</Text>
    </View>
  );
}

export function TermsAcceptancePdf({
  termsAcceptance,
  order,
  orderHistories,
  patientFirstName,
  patientLastName,
  patientDateOfBirth,
  facilityLogo,
  fullAddress,
  phoneNumber,
  facilityName,
  cardInfo,
}: ITermsAcceptancePdfProps) {
  const contextLabel =
    ACCEPTANCE_CONTEXT_LABELS[termsAcceptance.acceptanceContext] ??
    termsAcceptance.acceptanceContext;
  const acceptedAt = `${parseDate(
    termsAcceptance.createdAt,
    false,
    true
  )} at ${parseTime(termsAcceptance.createdAt, true)}`;

  const isPlanContext =
    termsAcceptance.acceptanceContext === TermsAcceptanceContext.DownPayment ||
    termsAcceptance.acceptanceContext === TermsAcceptanceContext.PayRemaining;
  const plan = termsAcceptance.payment?.paymentPlan;

  const actualPaymentAmountInCents = (() => {
    const { payment } = termsAcceptance;
    if (!payment || payment.amountInCents == null) return null;
    if (payment.splitTotalPaymentId) {
      const siblings = order.payments?.filter(
        (p) => p.splitTotalPaymentId === payment.splitTotalPaymentId
      );
      if (siblings && siblings.length > 0) {
        return siblings.reduce((sum, p) => sum + (p.amountInCents ?? 0), 0);
      }
    }
    return payment.amountInCents;
  })();

  const minDownPaymentLabel = (() => {
    if (!plan) return null;
    if (plan.paymentPlanMinDownPaymentFlat != null) {
      return parsePrice(centsToDollars(plan.paymentPlanMinDownPaymentFlat));
    }
    if (plan.paymentPlanMinDownPaymentPercent != null) {
      return `${plan.paymentPlanMinDownPaymentPercent}%`;
    }
    return null;
  })();

  const showDuration =
    !plan?.isDownPaymentOnly && plan?.paymentPlanDuration != null;

  const displayedText = (
    termsAcceptance.acceptanceSnapshot as {
      displayedText?: Record<string, string>;
    } | null
  )?.displayedText;

  return (
    <Document>
      <Page size="LETTER" style={{ ...styles.page, position: "relative" }}>
        <Header
          facilityLogo={facilityLogo}
          fullAddress={fullAddress}
          phoneNumber={phoneNumber}
        />

        <View style={styles.content} wrap>
          <View style={{ ...styles.section, marginTop: 0 }}>
            <Text style={styles.title}>Terms Acceptance</Text>
          </View>

          <View
            style={{
              ...styles.section,
              flexDirection: "row",
              justifyContent: "space-between",
              gap: 10,
            }}
          >
            <View style={{ width: "40%" }}>
              <InfoRow
                label="Patient"
                value={`${patientFirstName} ${patientLastName}`}
              />
              <InfoRow
                label="Date of Birth"
                value={parseDate(patientDateOfBirth)}
              />
            </View>

            {(isPlanContext ? !!plan : true) && (
              <View style={{ width: "60%" }}>
                <InfoRow
                  label="Payment type"
                  value={
                    ACCEPTANCE_CONTEXT_LABELS[termsAcceptance.acceptanceContext]
                  }
                  width={150}
                />
                {actualPaymentAmountInCents != null && (
                  <InfoRow
                    label="Actual Payment Amount"
                    value={parsePrice(
                      centsToDollars(actualPaymentAmountInCents)
                    )}
                    width={150}
                  />
                )}
                {plan?.calculationBasis && !plan.isDownPaymentOnly && (
                  <InfoRow
                    label="Calculation Basis"
                    value={
                      plan.calculationBasis === "InstallmentAmount"
                        ? "Fixed installment amount"
                        : plan.calculationBasis
                    }
                    width={150}
                  />
                )}
                {showDuration && (
                  <InfoRow
                    label="Duration"
                    value={`${plan?.paymentPlanDuration}`}
                    width={150}
                  />
                )}
                {plan?.installmentFrequency && !plan.isDownPaymentOnly && (
                  <InfoRow
                    label="Installment Frequency"
                    value={plan.installmentFrequency}
                    width={150}
                  />
                )}
                {minDownPaymentLabel && (
                  <InfoRow
                    label="Minimum Down Payment"
                    value={minDownPaymentLabel}
                    width={150}
                  />
                )}
              </View>
            )}
          </View>

          {displayedText && (
            <View
              style={{
                marginLeft: 50,
                marginRight: 50,
                marginTop: 12,
                padding: 16,
                borderWidth: 1,
                borderColor: "#e2e2e4",
                borderRadius: 8,
                display: "flex",
                flexDirection: "column",
              }}
              wrap={false}
            >
              {displayedText.paymentPlanAdjustment && (
                <View
                  style={{
                    borderWidth: 1,
                    borderColor: "#e2e2e4",
                    borderRadius: 6,
                    padding: 10,
                    marginBottom: 16,
                    flexDirection: "row",
                    alignItems: "flex-start",
                    gap: 8,
                  }}
                >
                  <View style={{ marginTop: 1 }}>
                    <InfoIcon />
                  </View>
                  <Text
                    style={{
                      fontSize: 10,
                      color: "#706f7b",
                      lineHeight: 1.4,
                      flex: 1,
                    }}
                  >
                    {displayedText.paymentPlanAdjustment}
                  </Text>
                </View>
              )}

              {displayedText.autopayNotice && (
                <View
                  style={{
                    borderWidth: 1,
                    borderColor: "#fedf89",
                    borderRadius: 6,
                    padding: 10,
                    marginBottom: 16,
                    backgroundColor: "#fffaeb",
                    flexDirection: "row",
                    alignItems: "flex-start",
                    gap: 8,
                  }}
                >
                  <View style={{ marginTop: 1 }}>
                    <InfoIcon />
                  </View>
                  <Text
                    style={{
                      fontSize: 10,
                      color: "#93370d",
                      lineHeight: 1.4,
                      flex: 1,
                    }}
                  >
                    {displayedText.autopayNotice}
                  </Text>
                </View>
              )}

              {displayedText.header && (
                <Text
                  style={{
                    fontSize: 16,
                    fontFamily: "Helvetica-Bold",
                    color: "#4f46e5",
                    textAlign: "center",
                    marginBottom: 4,
                  }}
                >
                  {displayedText.header}
                </Text>
              )}

              {displayedText.amount && (
                <Text
                  style={{
                    fontSize: 20,
                    fontFamily: "Helvetica-Bold",
                    color: "#4f46e5",
                    textAlign: "center",
                    marginBottom: 8,
                  }}
                >
                  {displayedText.amount}
                </Text>
              )}

              {displayedText.paymentPlanInstallments && (
                <Text
                  style={{
                    fontSize: 10,
                    color: "#64636e",
                    textAlign: "center",
                    marginBottom: 2,
                  }}
                >
                  {displayedText.paymentPlanInstallments}
                </Text>
              )}

              {displayedText.nextPaymentDue && (
                <Text
                  style={{
                    fontSize: 10,
                    color: "#64636e",
                    textAlign: "center",
                    marginBottom: 12,
                  }}
                >
                  {displayedText.nextPaymentDue}
                </Text>
              )}

              {displayedText.nextPaymentScheduled && (
                <Text
                  style={{
                    fontSize: 10,
                    color: "#64636e",
                    textAlign: "center",
                    marginBottom: 12,
                  }}
                >
                  {displayedText.nextPaymentScheduled}
                </Text>
              )}

              {termsAcceptance.acceptanceContext ===
                TermsAcceptanceContext.PayRemaining &&
                termsAcceptance.payment?.amountInCents != null && (
                  <View style={{ marginTop: 8, marginBottom: 4 }}>
                    <Text
                      style={{
                        fontSize: 10,
                        color: "#64636e",
                        marginBottom: 4,
                      }}
                    >
                      Custom Amount:
                    </Text>
                    <View
                      style={{
                        padding: 10,
                        borderWidth: 1,
                        borderColor: "#d2d6db",
                        borderRadius: 8,
                        backgroundColor: "#ffffff",
                      }}
                    >
                      <Text style={{ fontSize: 11, color: "#30313d" }}>
                        {parsePrice(
                          centsToDollars(termsAcceptance.payment.amountInCents)
                        )}
                      </Text>
                    </View>
                  </View>
                )}

              <View
                style={{
                  marginTop: 8,
                  padding: 14,
                  borderWidth: 1,
                  borderColor: "#d2d6db",
                  borderRadius: 8,
                  backgroundColor: "#f6f8fe",
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Text style={{ fontSize: 10, color: "#a5a4ad" }}>
                  Card details
                </Text>
              </View>

              <View
                style={{
                  marginTop: 12,
                  flexDirection: "row",
                  alignItems: "flex-start",
                  gap: 8,
                }}
              >
                <View style={{ marginTop: 1 }}>
                  <CheckedBox />
                </View>
                <Text
                  style={{
                    fontSize: 10,
                    color: "#858490",
                    lineHeight: 1.4,
                    flex: 1,
                  }}
                >
                  {`By clicking "Pay Now", I confirm that I have read and agree to the `}
                  <Text style={{ color: "#5b4dfd" }}>Privacy Policy</Text>
                  {termsAcceptance.privacyPolicyVersion
                    ? ` (version: ${
                        termsAcceptance.privacyPolicyVersion.version
                      }, effective from ${parseDate(
                        termsAcceptance.privacyPolicyVersion.effectiveAt
                      )})`
                    : ""}
                  {" and "}
                  <Text style={{ color: "#5b4dfd" }}>Terms and Conditions</Text>
                  {termsAcceptance.termsAndConditionsVersion
                    ? ` (version: ${
                        termsAcceptance.termsAndConditionsVersion.version
                      }, effective from ${parseDate(
                        termsAcceptance.termsAndConditionsVersion.effectiveAt
                      )})`
                    : ""}
                  .
                </Text>
              </View>

              {displayedText.paymentPlanAuthorization && (
                <Text
                  style={{
                    marginTop: 8,
                    fontSize: 10,
                    color: "#858490",
                    lineHeight: 1.4,
                    textAlign: "left",
                  }}
                >
                  {displayedText.paymentPlanAuthorization}
                </Text>
              )}

              {displayedText.secureTransactions && (
                <View
                  style={{
                    marginTop: 12,
                    flexDirection: "row",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 6,
                  }}
                >
                  <LockIcon />
                  <Text style={{ fontSize: 10, color: "#858490" }}>
                    {displayedText.secureTransactions}
                  </Text>
                </View>
              )}

              <View
                style={{
                  marginTop: 12,
                  paddingVertical: 12,
                  borderRadius: 8,
                  backgroundColor: "#5b4dfd",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Text
                  style={{
                    fontSize: 12,
                    fontFamily: "Helvetica-Bold",
                    color: "#ffffff",
                  }}
                >
                  Pay Now
                </Text>
              </View>
            </View>
          )}

          <View style={{ ...styles.section, marginTop: 16 }}>
            <InfoRow label="Context" value={contextLabel} width={120} />
            <InfoRow label="Accepted At" value={acceptedAt} width={120} />
            {termsAcceptance.ipAddress && (
              <InfoRow
                label="IP Address"
                value={termsAcceptance.ipAddress}
                width={120}
              />
            )}
            {termsAcceptance.userAgent && (
              <InfoRow
                label="User Agent"
                value={termsAcceptance.userAgent}
                width={120}
              />
            )}
          </View>
        </View>

        <Footer facilityName={facilityName} />
      </Page>

      <Page size="LETTER" style={{ ...styles.page, position: "relative" }}>
        <Header
          facilityLogo={facilityLogo}
          fullAddress={fullAddress}
          phoneNumber={phoneNumber}
        />
        <View style={styles.content} wrap>
          <PaymentSummarySection order={order} cardInfo={cardInfo} />
        </View>
        <Footer facilityName={facilityName} />
      </Page>

      {termsAcceptance.termsAndConditionsContent && (
        <Page size="LETTER" style={{ ...styles.page, position: "relative" }}>
          <Header
            facilityLogo={facilityLogo}
            fullAddress={fullAddress}
            phoneNumber={phoneNumber}
          />
          <View style={styles.content} wrap>
            <View style={{ ...styles.section, marginTop: 0 }}>
              <Text style={styles.title}>Terms of Use</Text>
              {termsAcceptance.termsAndConditionsVersion && (
                <View
                  style={{
                    flexDirection: "row",
                    justifyContent: "center",
                    gap: 16,
                    marginTop: 4,
                  }}
                >
                  <Text style={{ ...styles.text, fontSize: 10 }}>
                    {`Version: ${termsAcceptance.termsAndConditionsVersion.version}`}
                  </Text>
                  <Text style={{ ...styles.text, fontSize: 10 }}>
                    {`Effective Date: ${parseDate(
                      termsAcceptance.termsAndConditionsVersion.effectiveAt
                    )}`}
                  </Text>
                </View>
              )}
            </View>
            <View style={{ ...styles.section }}>
              <MarkdownPdf compact>
                {termsAcceptance.termsAndConditionsContent}
              </MarkdownPdf>
            </View>
          </View>
          <Footer facilityName={facilityName} />
        </Page>
      )}

      {termsAcceptance.privacyPolicyContent && (
        <Page size="LETTER" style={{ ...styles.page, position: "relative" }}>
          <Header
            facilityLogo={facilityLogo}
            fullAddress={fullAddress}
            phoneNumber={phoneNumber}
          />
          <View style={styles.content} wrap>
            <View style={{ ...styles.section, marginTop: 0 }}>
              <Text style={styles.title}>Privacy Policy</Text>
              {termsAcceptance.privacyPolicyVersion && (
                <View
                  style={{
                    flexDirection: "row",
                    justifyContent: "center",
                    gap: 16,
                    marginTop: 4,
                  }}
                >
                  <Text style={{ ...styles.text, fontSize: 10 }}>
                    {`Version: ${termsAcceptance.privacyPolicyVersion.version}`}
                  </Text>
                  <Text style={{ ...styles.text, fontSize: 10 }}>
                    {`Effective Date: ${parseDate(
                      termsAcceptance.privacyPolicyVersion.effectiveAt
                    )}`}
                  </Text>
                </View>
              )}
            </View>
            <View style={{ ...styles.section }}>
              <MarkdownPdf compact>
                {termsAcceptance.privacyPolicyContent}
              </MarkdownPdf>
            </View>
          </View>
          <Footer facilityName={facilityName} />
        </Page>
      )}

      {hasCommunicationHistory(orderHistories) && (
        <Page size="LETTER" style={{ ...styles.page, position: "relative" }}>
          <Header
            facilityLogo={facilityLogo}
            fullAddress={fullAddress}
            phoneNumber={phoneNumber}
          />
          <View style={styles.content} wrap>
            <OrderHistorySection orderHistories={orderHistories} />
          </View>
          <Footer facilityName={facilityName} />
        </Page>
      )}
    </Document>
  );
}
